/**
 * Algebra Basics: algebraic notation, simple linear equations.
 */

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const makeNumericDistractors = (correct, count = 3) => {
    const seen = new Set([correct]);
    const out = [];
    const base = Number(correct);
    const offsets = [-3, -2, -1, 1, 2, 3, 4];
    while (out.length < count) {
        const off = offsets[Math.floor(Math.random() * offsets.length)];
        const val = base + off;
        if (val !== base && !seen.has(String(val))) {
            seen.add(String(val));
            out.push(String(val));
        }
    }
    return out;
};

/** Using algebraic notation – match words to expressions */
export const generateAlgebraNotationQuestion = (difficulty = "normal") => {
    const templates = [
        {
            text: "3 multiplied by x",
            expr: "3x",
            distractors: ["x + 3", "x - 3", "x / 3"],
        },
        {
            text: "a plus 5",
            expr: "a + 5",
            distractors: ["a - 5", "5 - a", "a × 5"],
        },
        {
            text: "y minus 4",
            expr: "y - 4",
            distractors: ["y + 4", "4 - y", "4y"],
        },
        {
            text: "n divided by 2",
            expr: "n / 2",
            distractors: ["2n", "n + 2", "n - 2"],
        },
        {
            text: "5 more than t",
            expr: "t + 5",
            distractors: ["t - 5", "5t", "t / 5"],
        },
    ];

    const item = templates[Math.floor(Math.random() * templates.length)];
    const question = `Which expression means "${item.text}"?`;
    const answer = item.expr;
    const options = shuffle([answer, ...item.distractors]);

    return {
        question,
        answer,
        options,
        type: "algebra-notation",
        mode: "notation",
        visualType: "notation-match",
        phrase: item.text,
    };
};

/** Solving simple linear equations */
export const generateLinearEquationQuestion = (difficulty = "normal") => {
    const types =
        difficulty === "easy"
            ? ["add", "sub"]
            : difficulty === "hard"
            ? ["add", "sub", "mul", "div", "two-step"]
            : ["add", "sub", "mul", "div"];

    const choice = types[Math.floor(Math.random() * types.length)];
    let question;
    let answer;

    if (choice === "add") {
        const a = Math.floor(Math.random() * 9) + 2; // 2-10
        const x = Math.floor(Math.random() * 10) + 1; // 1-10
        const b = a + x;
        question = `Solve: x + ${a} = ${b}`;
        answer = String(x);
    } else if (choice === "sub") {
        const a = Math.floor(Math.random() * 9) + 2;
        const x = Math.floor(Math.random() * 10) + 5;
        const b = x - a;
        question = `Solve: x - ${a} = ${b}`;
        answer = String(x);
    } else if (choice === "mul") {
        const a = Math.floor(Math.random() * 4) + 2; // 2-5
        const x = Math.floor(Math.random() * 10) + 1;
        const b = a * x;
        question = `Solve: ${a}x = ${b}`;
        answer = String(x);
    } else if (choice === "div") {
        const a = Math.floor(Math.random() * 8) + 2; // 2-9
        const x = Math.floor(Math.random() * 10) + 1;
        const b = x / a;
        const num = a * x;
        question = `Solve: x / ${a} = ${b}`;
        answer = String(x);
        // To avoid fractions for b, we instead phrase as x / a = b with integer b
        // where x = a * b. Adjust:
        const newB = x;
        const newX = a * newB;
        question = `Solve: x / ${a} = ${newB}`;
        answer = String(newX);
    } else {
        // two-step: ax + c = d
        const a = Math.floor(Math.random() * 3) + 2; // 2-4
        const x = Math.floor(Math.random() * 8) + 1; // 1-8
        const c = Math.floor(Math.random() * 6) + 2; // 2-7
        const d = a * x + c;
        question = `Solve: ${a}x + ${c} = ${d}`;
        answer = String(x);
    }

    const options = shuffle([answer, ...makeNumericDistractors(Number(answer), 3)]);

    return {
        question,
        answer,
        options,
        type: "algebra-equations",
        mode: "equation",
        visualType: "equation-balance",
        equation: question.replace(/^Solve: /, ""),
    };
};

export const generateAlgebraBasicsQuestion = (gameKey, difficulty = "normal") => {
    switch (gameKey) {
        case "algebra-notation":
            return generateAlgebraNotationQuestion(difficulty);
        case "algebra-equations":
            return generateLinearEquationQuestion(difficulty);
        default:
            return generateAlgebraNotationQuestion(difficulty);
    }
};

